import {
  type Answers,
  type Phase,
} from '@/karehero-app/care/forms/FinancialAssessment';
import { makeSceneLookup, type Scene } from '@/designs-dev/scenes';

// The states the financial assessment can start in, each paired with the
// `initial` props that drive it there. Shared by the live state select in the
// inspect panel so the set of states is defined in one place.
export type FinancialAssessmentInitial = {
  initialPhase?: Phase;
  initialAnswers?: Answers;
  initialStepKey?: string;
  recipientKnown?: boolean;
};

// Populated answers for the review state.
const SAMPLE: Answers = {
  firstName: 'Margaret',
  lastName: 'Hughes',
  postcode: 'EH3 6HR',
  allergies: 'yes',
  allergyList: ['Milk', 'Pollen'],
  priorities: ['Increase mobility and physical activity', 'Be healthier'],
  illnesses: 'yes',
  illnessList: [
    {
      name: 'COPD',
      howLong: '3 years',
      medicines: 'Inhaler',
      dosage: 'Two puffs, twice a day',
      support: 'reminding',
    },
    {
      name: 'Arthritis',
      howLong: '10 years',
      medicines: 'Co-codamol',
      dosage: '300/500, as needed',
      support: 'none',
    },
  ],
  ownsHome: 'outright',
  whoElse: 'alone',
  homeValue: '320000',
  disregard: 'unsure',
  deferred: 'no',
  savings: '12000',
  assets: '40000',
  statePension: '11500',
  privatePension: '6200',
  otherIncome: 'no',
  receivingCare: 'yes',
  careCost: '1800',
  attendanceAllowance: 'lower',
  pensionCredit: 'no',
  pipDla: 'unsure',
  familyCaring35: 'yes',
  carerEarnings: 'no',
};

export const financialAssessmentScenes: Scene<FinancialAssessmentInitial>[] = [
  {
    id: 'intro',
    label: 'Intro',
    kind: 'happy',
    repro: 'The entry screen: what the assessment is for, then Start',
    initial: { initialPhase: 'intro' },
  },
  {
    id: 'name-confirm',
    label: 'First question (name on file)',
    kind: 'happy',
    repro:
      "First question with the recipient's name prefilled from her profile",
    initial: { initialPhase: 'form', recipientKnown: true },
  },
  {
    id: 'name-unknown',
    label: 'First question (no name on file)',
    kind: 'edge',
    repro:
      'They skipped the name at onboarding, so the first step asks for it rather than confirming one',
    initial: { initialPhase: 'form', recipientKnown: false },
  },
  {
    id: 'allergies-details',
    label: 'Allergies — details revealed',
    kind: 'edge',
    repro:
      "Answering 'yes' to allergies reveals the (optional) allergy multi-select beneath it",
    initial: {
      initialPhase: 'form',
      initialStepKey: 'allergies',
      initialAnswers: { allergies: 'yes', allergyList: ['Milk', 'Pollen'] },
    },
  },
  {
    id: 'home-details',
    label: 'Home ownership — follow-ups revealed',
    kind: 'happy',
    repro:
      'Owning the home reveals four follow-ups (who else lives there, value, disregard, deferred payment)',
    initial: {
      initialPhase: 'form',
      initialStepKey: 'ownsHome',
      initialAnswers: {
        ownsHome: 'outright',
        whoElse: 'alone',
        homeValue: '320000',
        disregard: 'unsure',
        deferred: 'no',
      },
    },
  },
  {
    id: 'illness-details',
    label: 'Illnesses — repeatable table',
    kind: 'happy',
    repro:
      "Answering 'yes' to illnesses reveals the required repeatable list; entries collapse to summary cards, add/edit in a bottom sheet",
    initial: {
      initialPhase: 'form',
      initialStepKey: 'illnesses',
      initialAnswers: {
        illnesses: 'yes',
        illnessList: [
          {
            name: 'COPD',
            howLong: '3 years',
            medicines: 'Inhaler',
            dosage: 'Two puffs, twice a day',
            support: 'reminding',
          },
          {
            name: 'Arthritis',
            howLong: '10 years',
            medicines: 'Co-codamol',
            dosage: '300/500, as needed',
            support: 'none',
          },
        ],
      },
    },
  },
  {
    id: 'review',
    label: 'Review answers',
    kind: 'happy',
    repro: 'The end-of-flow review list, populated and editable',
    initial: { initialPhase: 'review', initialAnswers: SAMPLE },
  },
];

export const financialAssessmentSceneById = makeSceneLookup(
  financialAssessmentScenes,
);
